// src/pages/Publisher/Gamics/Gamics.tsx
import React from 'react';
import FilterSelect from '@/components/filters/FilterSelect';
import Search from '@/components/filters/Search';
import AddNewGamicsButton from '@/components/buttons/AddNewGamicsButton';
import GamicsTable from './components/GamicsTable';
import ConfirmDialog from '@/components/dialog/ConfirmDialog';
import SuccessDialog from '@/components/dialog/SuccessDialog';
import { Gamic, Status } from './types';
import { Icons } from '@/icons';
import { useDispatch } from 'react-redux';
import { deleteGamic, fetchMyGamics } from '@/store/Gamic';
import { fetchCategories } from '@/store/Category';
import EditGamics from './EditGamics';

interface Category {
  value: string,
  label: string
}

const Gamics: React.FC = () => {
  const [category, setCategory] = React.useState<string>('All');
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [status, setStatus] = React.useState<string>('All');
  const [search, setSearch] = React.useState<string>('');
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [rowIdx, setRowIdx] = React.useState<string | null>(null);
  const [gamics, setGamics] = React.useState<Gamic[]>([]);

  const [deleteIdx, setDeleteIdx] = React.useState<string | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = React.useState<boolean>(false);
  const [isArchiveOpen, setIsArchiveOpen] = React.useState<boolean>(false);
  const [isDeleteSuccessOpen, setIsDeleteSuccessOpen] =
    React.useState<boolean>(false);
  const [isArchiveSuccessOpen, setIsArchiveSuccessOpen] =
    React.useState<boolean>(false);
  const dispatch = useDispatch<any>();

  // fetch gamic data
  React.useEffect(() => {
    const fetchGamic = async () => {
      const params: { [key: string]: any } = {};

      if (status && status !== 'All') {
        params.status = status;
      }

      if (category && category !== 'All') {
        params.categoryName = category;
      }

      if (search) {
        params.title = search;
      }

      const res = await dispatch(fetchMyGamics(params));
      setGamics(res.payload.data);
      
    };
    fetchGamic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, search, category]);

   React.useEffect(() => {
    const toTitleCase = (s: string) =>
  s.replace(/\w\S*/g, w =>
    w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  );

    const fetchCategory = async () => {
      const res = await dispatch(fetchCategories());
      const raw = res.payload.data as {name: string; id: string}[];
      const options = [
        { value: 'All', label: 'Category: All' },
        ...raw.map(cat => ({
          value: cat.name,
          label: `Category: ${toTitleCase(cat.name)}`
        }))
      ]
      setCategories(options);
      
    };
    fetchCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, idx: string) => {
    setAnchorEl(e.currentTarget);
    setRowIdx(idx);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setRowIdx(null);
  };

  const openDelete = (idx: string) => {
    handleMenuClose();
    setDeleteIdx(idx);
    setIsDeleteOpen(true);
  };
  const closeDelete = () => {
    setIsDeleteOpen(false);
    setDeleteIdx(null);
  };
  const confirmDelete = async () => {
    if (deleteIdx !== null) {
      await dispatch(deleteGamic(deleteIdx))
      setGamics((curr) => curr.filter((g) => g.id !== deleteIdx));
    }
    closeDelete();
    setIsDeleteSuccessOpen(true);
  };

  const openArchive = (idx: string) => {
    handleMenuClose();
    setIsArchiveOpen(true);
  };
  const closeArchive = () => {
    setIsArchiveOpen(false);
  };
  const confirmArchive = () => {
    // TODO: archive logic
    closeArchive();
    setIsArchiveSuccessOpen(true);
  };

  return (
    <div className='p-8 rounded-[24px] bg-white flex flex-col h-full min-h-0 space-y-[56px]'>
      {/* filters + search + add-button row */}
      <div className='flex flex-wrap items-center gap-4'>
        <FilterSelect
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          options={[...categories]}
        />

        <FilterSelect
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={[
            { value: 'All', label: 'Status: All' },
            { value: 'pending', label: 'Status: Pending' },
            { value: 'approved', label: 'Status: Approved' },
            { value: 'rejected', label: 'Status: Rejected' },
          ]}
        />

        <Search value={search} onChange={(e) => setSearch(e.target.value)} />

        <div className='ml-auto w-[226px] inline-flex items-center'>
          <AddNewGamicsButton
            to='/publisher/add-gamics'
            label='Add New Gamics'
            responsive={false}
          />
        </div>
      </div>

      {/* full-height table */}
      <div className='flex-1 min-h-0'>
        <GamicsTable
          data={gamics}
          anchorEl={anchorEl}
          rowIdx={rowIdx}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          onDelete={openDelete}
          onArchive={openArchive}
        />
      </div>

      {/* Confirm / Archive dialog */}
      <ConfirmDialog
        open={isDeleteOpen || isArchiveOpen}
        title={isDeleteOpen ? 'Delete Gamic' : 'Archive Gamic'}
        message={
          isDeleteOpen ? (
            <>
              Are you sure you want to delete this gamic? <br />
              You can’t undo this.
            </>
          ) : (
            'Are you sure you want to archive this gamic?'
          )
        }
        confirmLabel={isDeleteOpen ? 'Yes, Delete' : 'Yes, Archive'}
        cancelLabel='No, Cancel'
        requireAck
        onConfirm={isDeleteOpen ? confirmDelete : confirmArchive}
        onClose={isDeleteOpen ? closeDelete : closeArchive}
      />

      {/* Success dialog */}
      <SuccessDialog
        open={isDeleteSuccessOpen || isArchiveSuccessOpen}
        onClose={() => {
          setIsDeleteSuccessOpen(false);
          setIsArchiveSuccessOpen(false);
        }}
        icon={
          isDeleteSuccessOpen ? (
            <Icons.Trash className='h-[160px] w-[160px]' />
          ) : (
            <Icons.Archived className='h-[160px] w-[160px]' />
          )
        }
        heading={isDeleteSuccessOpen ? 'Deleted!' : 'Archived!'}
        subheading={
          isDeleteSuccessOpen
            ? 'Your gamic has been deleted.'
            : 'Your gamic has been archived.'
        }
      />
    </div>
  );
};

export default Gamics;
